import { FC, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonA } from "../../common/components/ButtonA";
import { ButtonALoading } from "../../common/components/ButtonALoading";
import StartIcon from "@mui/icons-material/Start";
import DownloadIcon from "@mui/icons-material/Download";
import { usePaginationTable } from "../../common/hooks/usePaginationTable";
import { TaskCounterSubmissionSearch } from "./TaskCounterSubmissionSearch";
import { TaskCounterAdminSearch } from "./TaskCounterAdminSearch";
import { TaskCounterSubmissionTable } from "./TaskCounterSubmissionTable";
import { SubmissionToggle } from "./SubmissionToggle";
import { useMutation } from "react-query";
import { fetchTaskCounterSubmissionList } from "../miscellaneous/fetchTaskCounterSubmission";
import { fetchExportTaskCounterSubmission } from "../miscellaneous/fetchExportTaskCounterSubmission";
import { TaskCounterSubmissionListItem } from "../types/TaskCounterTypes";
import { NotifyExportModal } from "../../task-submission/components/NotifyExportModal";
import { CapchaTokenModal } from "../../task-submission/components/CapchaTokenModal";
import dayjs from "dayjs";
import { useTaskCounterSubmissionSearchValue } from "../hooks/useTaskCounterSubmissionSearchValue";
import { useSetTaskCounterSubmissionSearch } from "../hooks/useSetTaskCounterSubmissionSearch";
import { defaultTaskCounterSubmissionSearchState } from "../miscellaneous/taskCounterSubmissionSearchAtom";

export const TaskCounterSubmissionList: FC = () => {
  const navigate = useNavigate();

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "admin";

  const isRoleSuperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const isAdmin = isRoleAdmin || isRoleSuperAdmin;

  const [submissions, setSubmissions] = useState<TaskCounterSubmissionListItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const searchAtomState = useTaskCounterSubmissionSearchValue();
  const setSearchAtomState = useSetTaskCounterSubmissionSearch();

  const [searchParams, setSearchParamsLocal] = useState<any>(searchAtomState.criteria);

  const setSearchParams = (newCriteria: any) => {
    setSearchParamsLocal(newCriteria);
    setSearchAtomState((prev) => ({ ...prev, criteria: newCriteria, hasSearched: true }));
  };

  const handleResetAtom = () => {
    setSearchAtomState(defaultTaskCounterSubmissionSearchState);
  };

  const [isShowNotifyModal, setIsShowNotifyModal] = useState(false);
  const [isShowCapchaModal, setIsShowCapchaModal] = useState(false);
  const [captChaToken, setCaptChaToken] = useState(null);

  const {
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage,
    setPage,
  } = usePaginationTable(submissions, searchAtomState.page, searchAtomState.pageSize);

  // Sync page/pageSize to atom
  useEffect(() => {
    setSearchAtomState((prev) => ({ ...prev, page, pageSize: rowsPerPage }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const mutationGetList = useMutation(fetchTaskCounterSubmissionList, {
    onSuccess: (response) => {
      if (response && response.status === 200) {
        setSubmissions(response.data.submissions);
        setTotalRecords(response.data.totalRecords);
      }
    },
  });

  const mutationExport = useMutation(fetchExportTaskCounterSubmission, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setIsShowNotifyModal(true);
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    const payload = isAdmin
      ? {
          pageNum: page + 1,
          pageSize: rowsPerPage,
          taskId: searchParams.taskId ?? 0,
          taskName: searchParams.taskName ?? "",
          from: searchParams.from
            ? dayjs(searchParams.from).format()
            : null,
          to: searchParams.to
            ? dayjs(searchParams.to).format()
            : null,
          companyId: searchParams.companyId ?? "",
          jobTitles: searchParams.jobTitles,
          parentCompanyId: searchParams.parentCompanyId ?? "",
          submittedByUserId: searchParams.submittedByUserId ?? "",
          timezone: dayjs().format(),
        }
      : {
          pageNum: page + 1,
          pageSize: rowsPerPage,
          taskId: searchParams.taskId ?? null,
          taskName: searchParams.taskName ?? "",
          workDate: searchParams.workDate
            ? dayjs(searchParams.workDate).format()
            : null,
          timezone: dayjs().format(),
        };

    mutationGetList.mutate(payload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, JSON.stringify(searchParams)]);

  const handleStartTask = () => {
    navigate(`/task-counter-screen?date=${dayjs().format("YYYY-MM-DD")}`);
  };

  const handleExportExcel = () => {
    if (!captChaToken) {
      setIsShowCapchaModal(true);
    } else {
      const payload = {
        pageNum: page,
        pageSize: rowsPerPage,
        taskId: searchParams.taskId ?? 0,
        taskName: searchParams.taskName ?? "",
        from: searchParams.from,
        to: searchParams.to,
        companyId: searchParams.companyId ?? "",
        jobTitles: searchParams.jobTitles,
        parentCompanyId: searchParams.parentCompanyId ?? "",
        submittedByUserId: searchParams.submittedByUserId ?? "",
        timezone: dayjs().format(),
      };
      mutationExport.mutate(payload);
    }
  };

  useEffect(() => {
    if (captChaToken) {
      const payload = {
        pageNum: page,
        pageSize: rowsPerPage,
        taskId: searchParams.taskId ?? 0,
        taskName: searchParams.taskName ?? "",
        from: searchParams.from,
        to: searchParams.to,
        companyId: searchParams.companyId ?? "",
        jobTitles: searchParams.jobTitles,
        parentCompanyId: searchParams.parentCompanyId ?? "",
        submittedByUserId: searchParams.submittedByUserId ?? "",
        timezone: dayjs().format(),
      };
      mutationExport.mutate(payload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShowCapchaModal]);

  const buttonExportJSX = () => {
    if (mutationExport.isLoading) {
      return <ButtonALoading name={"Export"} />;
    }
    return <ButtonA name={"Export"} icon={<DownloadIcon sx={{ fontSize: 18, marginRight: "5px" }} />} onHandleClick={handleExportExcel} />;
  };

  return (
    <>
      <div className="mt-9 mb-20">
        <div className="flex justify-between">
          <SectionTitle name="Task Submission" />
          <div className="flex flex-col items-end gap-2">
            {isAdmin ? (
              <>
                <SubmissionToggle activeMode="task-counter" />
                {buttonExportJSX()}
              </>
            ) : (
              <ButtonA
                name="Start Task"
                icon={<StartIcon sx={{ marginRight: "5px" }} />}
                onHandleClick={handleStartTask}
              />
            )}
          </div>
        </div>

        {isAdmin ? (
          <TaskCounterAdminSearch
            searchParams={searchParams}
            setSearchParams={setSearchParams}
            setPage={setPage}
            defaultValues={searchAtomState.criteria}
            onAtomReset={handleResetAtom}
          />
        ) : (
          <TaskCounterSubmissionSearch
            setSearchParams={setSearchParams}
            setPage={setPage}
            defaultValues={searchAtomState.criteria}
            onAtomReset={handleResetAtom}
          />
        )}

        <div className="mt-7">
          <TaskCounterSubmissionTable
            data={submissions}
            page={page}
            rowsPerPage={rowsPerPage}
            totalRecords={totalRecords}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            setData={setSubmissions}
            setTotalRecords={setTotalRecords}
            isLoading={mutationGetList.isLoading}
            isAdmin={isAdmin}
          />
        </div>
      </div>

      <NotifyExportModal
        isShowModal={isShowNotifyModal}
        setIsShowModal={setIsShowNotifyModal}
      />

      <CapchaTokenModal
        isShowModal={isShowCapchaModal}
        setIsShowModal={setIsShowCapchaModal}
        setCaptChaToken={setCaptChaToken as any}
        content={"Please verify you are human before export processing"}
      />
    </>
  );
};
